import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Tabs, StyledTab } from '@/components/Navigation/Subnavigation';
import {
  DropdownList,
  StyledDropdownItem
} from '@/components/Navigation/DropdownSubNav';

const SearchTabs = styled(Tabs)`
  border: 1px solid ${(props) => props.theme.colors.kuluttaja2021.harmaa[20]};
  display: flex;
  margin-top: 50px;
  > li {
    flex: auto;
  }
  ${(props) => props.theme.media.tablet`
    display: none;
  `};
`;

const SearchTabsDrop = styled(DropdownList)`
  display: none;
  position: relative;
  border: 1px solid ${(props) => props.theme.colors.kuluttaja2021.harmaa[20]};
  margin-top: 32px;
  ${(props) => props.theme.media.tablet`
    display: flex;
  `};
`;

/**
 * ProductSearchTabs component for displaying search tabs.
 * @param {Object} props - The component's props.
 * @param {Array} props.categories - An array of categories to display.
 * @param {string} props.selectedCategoryUrl - The URL of the selected category.
 * @param {Function} props.didClick - A callback function triggered when a tab is clicked.
 * @param {Function} props.children - A function to render the content based on the selected category.
 */
const ProductSearchTabs = ({
  categories,
  selectedCategoryUrl,
  didClick,
  children
}) => {
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [selected, setSelected] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(false);

  useEffect(() => {
    // Only run on first load
    if (!isFirstLoad || !parents) return;
    // Check if selectedCategoryUrl is in categories
    const initialCategory = parents.find(
      (cat) => cat.name.toLowerCase() === selectedCategoryUrl
    );

    // If selected state not set and initial category is set by url, set selected state
    if (!selected && initialCategory) {
      setSelected(initialCategory);
    }

    setIsFirstLoad(false);
  }, [selected]);

  /**
   * Toggle the dropdown for selecting a category.
   * @param {Object} category - The selected category.
   */
  const toggleDropdown = (category) => {
    setSelected(category);
    setOpenDropdown(!openDropdown);
    didClick(category);
  };

  if (!categories?.length) {
    return null;
  }

  const parents = categories
    .filter((cat) => !cat.parent)
    .sort((a, b) => a.menu_order - b.menu_order);

  if (!parents.length) {
    return null;
  }

  return (
    <div>
      <SearchTabs>
        <StyledTab
          onClick={() => {
            setSelected(null);
            didClick(null);
          }}
          $active={selected === null}
        >
          <FormattedMessage id="search.tabs.all" />
        </StyledTab>
        {parents.map((category) => (
          <StyledTab
            key={category.id}
            onClick={() => {
              setSelected(category);
              didClick(category);
            }}
            $active={selected && selected.id === category.id}
          >
            <span>{category.name}</span>
          </StyledTab>
        ))}
      </SearchTabs>

      <SearchTabsDrop $active={openDropdown}>
        <StyledDropdownItem
          $order={2}
          onClick={() => toggleDropdown(null)}
          $active={selected === null}
        >
          <FormattedMessage id="search.tabs.all" />
        </StyledDropdownItem>
        {parents.map((category, index) => (
          <StyledDropdownItem
            key={index}
            $order={2 + index}
            $active={selected && selected.id === category.id}
            onClick={() => toggleDropdown(category)}
          >
            <span>{category.name}</span>
          </StyledDropdownItem>
        ))}
      </SearchTabsDrop>

      <div>{children(selected)}</div>
    </div>
  );
};

ProductSearchTabs.propTypes = {
  /**
   * An array of categories to display.
   */
  categories: PropTypes.arrayOf(PropTypes.object),
  /**
   * The URL of the selected category.
   */
  selectedCategoryUrl: PropTypes.string,
  /**
   * A callback function triggered when a tab is clicked.
   */
  didClick: PropTypes.func,
  /**
   * A function to render the content based on the selected category.
   */
  children: PropTypes.func.isRequired
};

ProductSearchTabs.defaultProps = {
  categories: [],
  selectedCategoryUrl: '',
  didClick: () => {}
};

export default ProductSearchTabs;
